import { faPlusCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { FORUM_ADD_CONTEST, SEO_DESCRIPTION_DEFAULT_FALLBACK, SEO_TITLE_CONTESTS } from 'mk/autogenerated/translations/ContestsHomePage.9fff334aec17cd6111a9d84c06110d40'
import { contestsTopicSlug } from 'mk2/apps/contests/constants';
import { contestsHomeUrl } from 'mk2/apps/contests/urls';
import { forumReadFeedTrigger } from 'mk2/apps/forum/actions';
import { ContestEntity, ContestSchema } from 'mk2/apps/forum/schemas';
import { forumContestNewUrl } from 'mk2/apps/forum/urls';
import { Btn, BtnType } from 'mk2/components/Btn';
import { memoizeOne } from 'mk2/helpers/cache';
import { MapDispatchToPropsObject } from 'mk2/helpers/types';
import { url } from 'mk2/helpers/urls';
import Page from 'mk2/pages/Page';
import {
    getPageHeaderState,
    getRequestDeviceMobile,
    getRequestPermissions,
    getRequestUser,
    AppState,
} from 'mk2/reducers';
import { LoadingState } from 'mk2/reducers/loadingState';
import { UserEntity } from 'mk2/schemas';
import { getDenormalizedEntities } from 'mk2/selectors';
import { Store } from 'mk2/store/configureStore';
import { parse } from 'query-string';
import React from 'react';
import { MapStateToPropsParam } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import ContestsHome from './ContestsHome';
import { contestsHomeLoad, contestsHomeSetFollowing } from './ContestsHome.actions';
import contestsHomePageReducer, { ContestsHomePageState } from './ContestsHome.reducers';
import saga from './ContestsHome.sagas';
import { getContestsHomePageState } from './ContestsHome.selectors';

const fasPlusCircleLg: FontAwesomeIconProps = {icon: faPlusCircle, size: 'lg'};

interface OwnProps {}

interface StateProps {
    after: number;
    draftContests: ContestEntity[];
    errorMessage: string;
    finishedContests: ContestEntity[];
    hasMore: boolean;
    hasPrevious: number;
    isFollowing: boolean;
    isMobile: boolean;
    loadingState: LoadingState;
    requestUser: UserEntity;
    requestUserPermissions: string[];
    runningAmbContests: ContestEntity[];
    runningMkContests: ContestEntity[];
    setFollowingLoadingState: LoadingState;
    unreadCount: number;
}

interface DispatchProps {
    onLoad(after?: number);
    onRead();
    onSetFollowing(value: boolean);
}

type RouteProps = RouteComponentProps<{}>;
export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class ContestsHomePage extends Page<OwnProps, StateProps, DispatchProps> {

    private getMkContests = memoizeOne((contests) => contests.filter((contest) => contest.postId));
    private getAmbContests = memoizeOne((contests) => contests.filter((contest) => contest.blogPostId));

    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const parsed = parse(ownProps.location.search, { parseNumbers: true });
            const after = parsed.after as number;

            const pageHeaderState = getPageHeaderState(state);
            const pageState: ContestsHomePageState = getContestsHomePageState(state);

            const draftContests = getDenormalizedEntities<ContestEntity>(state, ContestSchema, pageState.drafts);
            const runningContests = getDenormalizedEntities<ContestEntity>(state, ContestSchema, pageState.runningContests);
            const finishedContests = getDenormalizedEntities<ContestEntity>(state, ContestSchema, pageState.ids);

            const runningMkContests = this.getMkContests(runningContests);
            const runningAmbContests = this.getAmbContests(runningContests);

            return {
                after,
                draftContests,
                errorMessage: pageState.errorMessage,
                finishedContests,
                hasMore: pageState.hasMore,
                hasPrevious: pageState.hasPrevious,
                isFollowing: pageState.isFollowing,
                isMobile: getRequestDeviceMobile(state),
                loadingState: pageState.loadingState,
                requestUser: getRequestUser(state),
                requestUserPermissions: getRequestPermissions(state),
                runningAmbContests,
                runningMkContests,
                setFollowingLoadingState: pageState.setFollowingLoadingState,
                unreadCount: pageHeaderState.userData.newContests,
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onLoad: contestsHomeLoad,
            onRead: () => forumReadFeedTrigger('contests'),
            onSetFollowing: contestsHomeSetFollowing,
        };
    }

    public getMetaTitle(props: Props): string {
        return SEO_TITLE_CONTESTS;
    }

    public getMetaDescription(props: Props): string {
        return SEO_DESCRIPTION_DEFAULT_FALLBACK;
    }

    public getCanonicalUrl(baseUrl: string, props: Props): string {
        return baseUrl + contestsHomeUrl;
    }

    public render(props: Props): false | JSX.Element {
        return (
            <span>
                <ContestsHome {...props} />
            </span>
        );
    }

    public hasHeaderBackButton(props: Props): boolean {
        return false;
    }

    public getHeaderRight(props: Props): React.ReactElement {
        const { requestUserPermissions } = props;
        const isAdmin = requestUserPermissions.indexOf('forum.can_manage_forum') >= 0;

        const addBtn = isAdmin ? (
            <Btn
                key="btn"
                type={BtnType.AdminOutline}
                label={FORUM_ADD_CONTEST}
                icon={fasPlusCircleLg}
                link={url(forumContestNewUrl, { topicSlug: contestsTopicSlug })}
            />
        ) : null;

        return addBtn;
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer('containers.contests.home', contestsHomePageReducer);
        store.injectSaga('containers.contests.home', saga);
    }

}
